import type { BuiltInTool, BuiltInToolError, BuiltInToolNeeds } from './types'

/**
 * The whole `workspace.*` surface. The runner's dispatcher (`WorkspaceTools.invoke`) switches exhaustively over
 * this union, and the registry below is typed by it, so a name added here without a definition, or a definition
 * without a name, fails the typecheck.
 */
export const WORKSPACE_TOOL_NAMES = [
    'workspace.read_file',
    'workspace.list_dir',
    'workspace.write_file',
    'workspace.delete_file',
    'workspace.edit',
    'workspace.search',
    'workspace.run_command',
    'workspace.verified_results',
    'workspace.git_commit',
    'workspace.git_push',
    'workspace.share_file',
] as const

export type WorkspaceToolName = (typeof WORKSPACE_TOOL_NAMES)[number]

/** The binaries `workspace.run_command` may start. The runner refuses any other `argv[0]`. */
export const WORKSPACE_BINS = ['git', 'pnpm', 'npm', 'node', 'mise'] as const

const CLONE: BuiltInToolNeeds['input'] =
    "A `repo` input on the run — a git remote address, or whatever short form the code host binding's `git.repoTemplate` custom prop expands — which the runner clones. A code host binding may name the repository instead, in its `git.repo` prop. A task without `workspace.*` tools never gets a clone."

const WRITE_POLICY =
    "The task version's `workspace.writeAllow`, `rewriteAllow`, `writeDeny` and `appendOnly` decide which paths it may change; `workspace.pushAllow` decides which refs besides its own branch it may push."

const EXPLORING: BuiltInToolError = {
    code: 'exploration_budget_exceeded',
    when: 'Too many reads back over ground already covered with no write, edit or commit in between, or more distinct files, folders and searches than the per-run budget.',
    handling: 'tool_error',
}

const WRITE_REFUSED: BuiltInToolError = {
    code: 'workspace_write_not_allowed',
    when: "The path is outside the task's write globs, or matches `writeDeny`.",
    handling: 'tool_error',
}

const APPEND_ONLY: BuiltInToolError = {
    code: 'workspace_append_only',
    when: 'The path is add-only for this task, and the call would change or remove a line it had when the run started. A run holding a `repairGrant` (a recovery-authorised repair handed to the run a `suite_repairable` edge started) may change or remove exactly the lines it names, while each still reads as the grant recorded it; the refusal then says which lines fell outside the grant, that the grant is stale, or that no grant covers the path. A refusal for a run holding no grant ends by saying why none is in effect: no successor edge started the run, the edge it started from declares no `repairGrant` carry, the result that started it carried none, or the grant in its input is malformed. A grant never makes a delete, a `writeDeny` path or a path outside the write globs writable.',
    handling: 'tool_error',
}

/** The `workspace.*` definitions, keyed by name so a missing one is a type error. */
export const WORKSPACE_TOOLS: { readonly [Name in WorkspaceToolName]: BuiltInTool & { name: Name } } = {
    'workspace.read_file': {
        name: 'workspace.read_file',
        family: 'workspace',
        summary: 'Read a file from the clone, whole or one numbered window of it.',
        description:
            'Read a file from the run workspace. Pass path for the whole file, or add startLine (1-based) and limit to read one numbered window of a large file. Prefer a window over a scratch script.',
        inputSchema: {
            type: 'object',
            properties: {
                path: { type: 'string' },
                startLine: { type: 'number', description: 'First line to read, 1-based. Omit to read the whole file.' },
                limit: { type: 'number', description: 'How many lines to read from startLine. Default 200.' },
            },
            required: ['path'],
            additionalProperties: true,
        },
        returns: 'The file as UTF-8 text, or the requested window of it with line numbers.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { input: CLONE },
        errors: [EXPLORING],
        examples: [{ input: { path: 'src/index.ts' } }, { input: { path: 'src/index.ts', startLine: 200, limit: 80 } }],
        public: true,
    },
    'workspace.list_dir': {
        name: 'workspace.list_dir',
        family: 'workspace',
        summary: 'List a directory in the clone.',
        description: 'List a directory in the run workspace. Pass path (default .).',
        inputSchema: {
            type: 'object',
            properties: { path: { type: 'string' } },
            required: [],
            additionalProperties: true,
        },
        returns: 'The directory listing.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { input: CLONE },
        errors: [EXPLORING],
        examples: [{ input: { path: 'src' } }],
        public: true,
    },
    'workspace.write_file': {
        name: 'workspace.write_file',
        family: 'workspace',
        summary: 'Write a whole file in the clone.',
        description: 'Write a file in the run workspace. Pass path and content.',
        inputSchema: {
            type: 'object',
            properties: { path: { type: 'string' }, content: { type: 'string' } },
            required: ['path', 'content'],
            additionalProperties: true,
        },
        returns: '`{ ok: true }`.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Writing the same content again leaves the same file.',
        needs: { input: CLONE, writePolicy: WRITE_POLICY },
        errors: [WRITE_REFUSED, APPEND_ONLY],
        examples: [{ input: { path: 'src/greeting.ts', content: "export const greeting = 'hello'\n" } }],
        public: true,
    },
    'workspace.delete_file': {
        name: 'workspace.delete_file',
        family: 'workspace',
        summary: 'Delete a file from the clone outright.',
        description: 'Delete a file in the run workspace outright, instead of overwriting it empty. Pass path.',
        inputSchema: {
            type: 'object',
            properties: { path: { type: 'string' } },
            required: ['path'],
            additionalProperties: true,
        },
        callers: ['run'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'A repeat finds nothing to delete and fails `delete_file_missing`; the file stays deleted.',
        needs: { input: CLONE, writePolicy: WRITE_POLICY },
        errors: [
            { code: 'delete_file_missing', when: 'Nothing is at that path.', handling: 'tool_error' },
            { code: 'delete_file_is_directory', when: 'The path is a directory.', handling: 'tool_error' },
            WRITE_REFUSED,
            APPEND_ONLY,
        ],
        examples: [{ input: { path: 'src/unused.ts' } }],
        public: true,
    },
    'workspace.edit': {
        name: 'workspace.edit',
        family: 'workspace',
        summary: 'Replace one exact piece of text in a file.',
        description:
            'Replace one exact piece of text in a workspace file. Pass path, oldString and newString. oldString must appear exactly once unless you pass replaceAll. Use this to change a file, not write_file, which rewrites the whole file. An empty oldString on a path that does not exist creates the file with newString as its content; on a file that exists it is refused.',
        inputSchema: {
            type: 'object',
            properties: {
                path: { type: 'string' },
                oldString: { type: 'string', description: 'Exact text to replace, whitespace included.' },
                newString: { type: 'string', description: 'Replacement text. Empty string deletes oldString.' },
                replaceAll: { type: 'boolean', description: 'Replace every occurrence instead of requiring one.' },
            },
            required: ['path', 'oldString', 'newString'],
            additionalProperties: true,
        },
        callers: ['run'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'A repeat no longer finds oldString and fails `edit_no_match`; the first edit stands. A repeated create (empty oldString) finds the file it made and fails `edit_old_string_required`; the first create stands.',
        needs: { input: CLONE, writePolicy: WRITE_POLICY },
        errors: [
            {
                code: 'edit_old_string_required',
                when: '`oldString` is missing, or it is empty and either `newString` is missing or the file already exists. Pass both as strings: quote the text to replace, pass an empty `oldString` with the content as `newString` to create a missing file, or use `workspace.write_file` to rewrite the whole file.',
                handling: 'tool_error',
            },
            { code: 'edit_no_match', when: 'The file does not contain `oldString` exactly.', handling: 'tool_error' },
            {
                code: 'edit_not_unique',
                when: '`oldString` appears more than once and `replaceAll` is not set.',
                handling: 'tool_error',
            },
            {
                code: 'edit_file_missing',
                when: 'Nothing is at that path and `oldString` is not empty. To create the file, use `workspace.write_file`, or pass an empty `oldString`.',
                handling: 'tool_error',
            },
            WRITE_REFUSED,
            APPEND_ONLY,
        ],
        examples: [{ input: { path: 'src/greeting.ts', oldString: "'hello'", newString: "'hello, world'" } }],
        public: true,
    },
    'workspace.search': {
        name: 'workspace.search',
        family: 'workspace',
        summary: 'Search the clone for a string or pattern.',
        description:
            'Search the run workspace for a string. Pass pattern, optionally globs (e.g. ["apps/api/**/*.ts"]), ignoreCase, regex, maxResults. Returns path, line and text per match. Use this instead of writing a script to grep.',
        inputSchema: {
            type: 'object',
            properties: {
                pattern: { type: 'string' },
                globs: { type: 'array', items: { type: 'string' }, description: 'Limit to these path globs.' },
                ignoreCase: { type: 'boolean' },
                regex: { type: 'boolean', description: 'Treat pattern as a POSIX extended regexp.' },
                maxResults: { type: 'number', description: 'Default 100, ceiling 500.' },
            },
            required: ['pattern'],
            additionalProperties: true,
        },
        returns: 'Path, line and text for each match.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { input: CLONE },
        errors: [
            { code: 'search_pattern_required', when: '`pattern` is missing or empty.', handling: 'tool_error' },
            EXPLORING,
        ],
        examples: [{ input: { pattern: 'TODO', globs: ['src/**/*.ts'], maxResults: 20 } }],
        public: true,
    },
    'workspace.run_command': {
        name: 'workspace.run_command',
        family: 'workspace',
        summary: 'Run one allowlisted binary in the clone, with no shell.',
        description: `Run an allowlisted binary in the run workspace. Pass argv as a JSON array of strings. Example: {"argv":["git","status"]}. No shell, no pipes, no cd. Allowed bins: ${WORKSPACE_BINS.join(', ')}. The command's environment carries the values the task version's workspace.credentials name, each a bound tool's secret or custom prop under the variable the version chose; you never read a value, and one a command prints comes back masked.`,
        inputSchema: {
            type: 'object',
            properties: {
                argv: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'argv[0] is the binary. Example ["pnpm","test"]',
                },
            },
            required: ['argv'],
            additionalProperties: true,
        },
        returns: '`{ code, stdout, stderr }`.',
        callers: ['run'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'The command runs again.',
        needs: { input: CLONE },
        errors: [
            {
                code: 'argv_required',
                when: '`argv` is missing or is not an array of strings.',
                handling: 'tool_error',
                hint: 'workspace.run_command requires argv as a JSON array of strings, e.g. {"argv":["git","status"]}. No shell pipes.',
            },
            {
                code: 'bin_not_allowlisted',
                when: `\`argv[0]\` is not one of ${WORKSPACE_BINS.join(', ')}, or is a path.`,
                handling: 'tool_error',
            },
            {
                code: 'git_push_via_run_command',
                when: 'The command would push. Push with `workspace.git_push`.',
                handling: 'tool_error',
            },
            {
                code: 'git_hooks_skip_refused',
                when: "The command would skip the repo's git hooks (`--no-verify`, `core.hooksPath`).",
                handling: 'tool_error',
            },
            {
                code: 'direct_typecheck_refused',
                when: "The command runs a typechecker (`tsc`), a task-graph runner (`turbo`) or a package-manager typecheck script directly, around the repo's verification task. Verify with `mise run os:red <issue>`, which returns the typecheck's exit code and compiler output; `mise run <task>` is never refused.",
                handling: 'tool_error',
            },
            {
                code: 'workspace_credential_missing',
                when: 'The version asks for a credential (`workspace.credentials`) no binding on the run’s layers answers: the tool is not bound, or its binding holds no secret or lacks the named custom prop. Checked before the run starts, and again at each command.',
                handling: 'fail',
            },
            {
                code: 'workspace_credential_unavailable',
                when: 'A credential the version asks for is bound but cannot be read: the secret store is down or the secret no longer decrypts. The command is not run.',
                handling: 'fail',
            },
            {
                code: 'scratch_execution_refused',
                when: "The command would execute a file this run created outside the task's full-write paths, whether run directly or loaded by an inline script (`node -e \"require('./tmp.mjs')\"`), and whether named in full or the way Node resolves it (`node tmp`, `require('./tmp')`, a directory's `index`). Use `workspace.read_file`, `workspace.search` or `mise run os:red <issue>` instead. Files already in the clone, and files in the full-write paths, run as before.",
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { argv: ['git', 'status'] } }, { input: { argv: ['npm', 'test'] } }],
        public: true,
    },
    'workspace.verified_results': {
        name: 'workspace.verified_results',
        family: 'workspace',
        summary: "What the repo's `mise run` commands already returned on the clone's current commit in this pipeline.",
        description:
            "Read what the repo's `mise run <task>` commands already returned on the code this clone has now, in this run or an earlier run of the same pipeline (the run before it, or the one it resumes). Answers { commit, clean, results }: commit is the clone's HEAD now, clean is true when its tree has no tracked or untracked change outside ignored files, and results are the commands recorded at exactly that commit, each { task, part, args, exitCode, clean, runId, at }. A result stands for this code only when it and the clone are both clean: then exitCode 0 means that command, or that --part of it, already passed on exactly this code in this pipeline, and you need not run it again. When the clone is not clean, or a result was recorded on a changed tree, it says nothing about the code you have: run the command. A command run in parts whose final verdict part reads files its earlier parts wrote in the same clone cannot be resumed part by part from these records, because those files are not in this clone: reuse it only whole, when its verdict part is recorded as passed, and otherwise run all of its parts. Pass task, and args (the words after the task, flags left out, e.g. the issue number), to narrow the list.",
        inputSchema: {
            type: 'object',
            properties: {
                task: { type: 'string', description: 'Only results of this mise task, e.g. "os:check".' },
                args: {
                    type: 'array',
                    items: { type: 'string' },
                    description:
                        'Only results run with exactly these words after the task, flags left out, e.g. ["148"].',
                },
            },
            additionalProperties: false,
        },
        returns:
            "`{ commit, clean, results }`: the clone's HEAD and whether its tree is clean now, and each result recorded at that commit, oldest first, as `{ task, part, args, exitCode, clean, runId, at }`. `part` is the value after `--part`, or null.",
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { input: CLONE },
        errors: [
            {
                code: 'verified_results_filter_invalid',
                when: '`task` is not a string, or `args` is not an array of strings.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: {} }, { input: { task: 'os:check', args: ['148'] } }],
        public: true,
    },
    'workspace.git_commit': {
        name: 'workspace.git_commit',
        family: 'workspace',
        summary: 'Commit the tracked changes and the files this run wrote.',
        description:
            'Commit in the run workspace. Pass message. Stages tracked changes and the files you wrote or edited; a new file only a command created may be left out, so `git add` it with workspace.run_command first if it belongs in the commit.',
        inputSchema: {
            type: 'object',
            properties: { message: { type: 'string' } },
            required: ['message'],
            additionalProperties: true,
        },
        callers: ['run'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'A repeat makes another commit, even an empty one.',
        needs: { input: CLONE, writePolicy: WRITE_POLICY },
        errors: [
            {
                code: 'workspace_commit_not_allowed',
                when: "The commit would include paths outside the task's write globs, or, in a merge, a path the task may not write in full that is not the version the merge calls for: main's where only main changed it, the branch's where only the branch did, and both sides' changes with no line of the run's own where both did. An add-only path both sides changed may instead keep every line the branch head has, in order, but not while it still holds a conflict-marker line neither side has: that conflict is unresolved. The message names the rule, then each path in full, then the fix. Nothing is committed; a merge stays in progress.",
                handling: 'tool_error',
            },
            {
                code: 'workspace_append_only',
                when: "The commit changes or removes a line an add-only path had when the run started, other than a line the run's `repairGrant` names. In a merge, an add-only path both sides changed drops or edits a line the branch head has: it may add lines and take or leave main's, but keeps every branch-head line in order. Nothing is committed; a merge stays in progress.",
                handling: 'tool_error',
            },
            {
                code: 'git_commit_failed',
                when: 'git refused the commit, for example a pre-commit hook failed.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { message: 'Add the greeting' } }],
        public: true,
    },
    'workspace.git_push': {
        name: 'workspace.git_push',
        family: 'workspace',
        summary: 'Push the current branch, or one ref the task may push, never the default one unless named.',
        description:
            "Push HEAD to the current non-default branch. No arguments, usually. With `ref`, a full ref (`refs/heads/production`, `refs/tags/v1.2.3`), HEAD is pushed there instead, only when one of the task version's `workspace.pushAllow` globs matches it; that is the one way a run pushes the default branch, a tag or a branch a host builds from, and never a force push. Answers `{ ok, branch, sha, ref? }`: the branch, the commit that went up and the ref it went to, so a run that verified the tree before pushing can name that commit in the note it leaves on the pull request.",
        inputSchema: {
            type: 'object',
            properties: {
                ref: {
                    type: 'string',
                    description:
                        'A full ref to push HEAD to instead of the current branch (`refs/heads/…` or `refs/tags/…`), allowed only when `workspace.pushAllow` matches it.',
                },
            },
            additionalProperties: true,
        },
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A repeat with nothing new to push changes nothing.',
        needs: {
            input: CLONE,
            writePolicy: WRITE_POLICY,
            binding:
                "A code host tool bound in the run's cascade whose `git.host` custom prop matches the remote: the push authenticates with that binding's credential, and with no other.",
        },
        errors: [
            {
                code: 'default_branch_refused',
                when: 'The current branch is the default branch, and no `ref` named it under `workspace.pushAllow`.',
                handling: 'tool_error',
            },
            {
                code: 'push_ref_not_allowed',
                when: "A `ref` was given that none of the task version's `workspace.pushAllow` globs matches, or the version names none.",
                handling: 'tool_error',
            },
            {
                code: 'push_ref_invalid',
                when: 'A `ref` was given that is not a full branch or tag ref.',
                handling: 'tool_error',
            },
            APPEND_ONLY,
            {
                code: 'git_push_failed',
                when: "git refused the push, for example the repo's pre-push hook failed.",
                handling: 'tool_error',
            },
        ],
        public: true,
    },
    'workspace.share_file': {
        name: 'workspace.share_file',
        family: 'workspace',
        summary: 'Share one file from the clone at a public address that stops working after an hour, or up to 72.',
        description:
            'Share one file the run made in its workspace (a rendered video, an image, a PDF) at a public https address, so a bound tool that takes a file by address can fetch it, or a person can open it from a gate. Pass path, and hours (1 to 72, one when left out) when a person must see it later, such as a preview a gate waits on. Answers `{ url, expiresAt, bytes, contentType }`: hand `url` to the tool that needs the file. Anyone holding the address can read the file until it expires, so share only what is meant to leave the run, and only for as long as it is needed.',
        inputSchema: {
            type: 'object',
            properties: {
                path: { type: 'string', description: 'The file to share, relative to the workspace root.' },
                hours: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 72,
                    description: 'How long the address works, in hours: 1 when left out, at most 72.',
                },
            },
            required: ['path'],
            additionalProperties: true,
        },
        returns:
            "`{ url, expiresAt, bytes, contentType }`: the public address, when it stops working (ISO 8601), the file's size and the type it is served with.",
        callers: ['run'],
        effect: 'write',
        sideEffects:
            "Stores a copy of the file with the run's artifacts, counted towards the enterprise's storage, and makes it readable at the address until it expires.",
        retry: 'unsafe',
        retryNote:
            'Each call stores another copy and answers a new address; the earlier one keeps working until it expires.',
        needs: { input: CLONE },
        errors: [
            { code: 'share_file_missing', when: 'Nothing is at that path.', handling: 'tool_error' },
            { code: 'share_file_is_directory', when: 'The path is a folder, not a file.', handling: 'tool_error' },
            {
                code: 'share_file_hours_invalid',
                when: 'hours is not a whole number from 1 to 72.',
                handling: 'tool_error',
            },
            {
                code: 'share_file_too_large',
                when: 'The file is larger than 512 MB.',
                handling: 'tool_error',
            },
            { code: 'path_escape', when: 'The path leads outside the workspace.', handling: 'tool_error' },
        ],
        examples: [
            {
                input: { path: 'out/article-video.mp4' },
                note: 'Then pass the answered url to the tool that uploads it.',
            },
        ],
        public: true,
    },
}
